import axios from "axios"
import { handleDashboard } from "@store/actions/dashboard"
import { handleBabel, handleUserData } from "@store/actions/auth"
import usersViewConfigBabel from "../configs/usersViewConfigBabel"
import permissionsFormated from "../Components/permissions"
import endpointsConfig from '@configs/endpointsConfig'

// ** Checks if an object is empty (returns boolean)
export const isObjEmpty = obj => obj === null || Object.keys(obj).length === 0

// ** Returns K format from a number
export const kFormatter = num => (num > 999 ? `${(num / 1000).toFixed(1)}k` : num)

// ** Converts HTML to string
export const htmlToString = html => html.replace(/<\/?[^>]+(>|$)/g, '')

// ** Checks if the passed date is today
const isToday = date => {
  const today = new Date()
  return (
    /* eslint-disable operator-linebreak */
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
    /* eslint-enable */
  )
}

/**
 ** Format and return date in Humanize format
 ** Intl docs: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/format
 ** Intl Constructor: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
 * @param {String} value date to format
 * @param {Object} formatting Intl object to format with
 */
export const dateFormat = (value, formatting = { month: 'short', day: 'numeric', year: 'numeric' }, region = 'en-US') => {
  if (!value) return value
  return new Intl.DateTimeFormat(region, formatting).format(new Date(value))
}

// ** Returns short month of passed date
export const formatDateToMonthShort = (value, toTimeForCurrentDay = true, region = 'en-US') => {
  const date = new Date(value)
  let formatting = { month: 'short', day: 'numeric' }

  if (toTimeForCurrentDay && isToday(date)) {
    formatting = { hour: 'numeric', minute: 'numeric' }
  }

  return new Intl.DateTimeFormat(region, formatting).format(new Date(value))
}

export const dateISOFormat = (dateVal) => {
  const offset = dateVal.getTimezoneOffset()
  dateVal = new Date(dateVal.getTime() - (offset * 60 * 1000))
  return dateVal.toISOString().split('T')[0]
}

/**
 ** Return if user is logged in
 ** This is completely up to you and how you want to store the token in your frontend application
 *  ? e.g. If you are using cookies to store the application please update this function
 */
export const isUserLoggedIn = () => localStorage.getItem('userData')
export const getUserData = () => JSON.parse(localStorage.getItem('userData'))

export const clearAnalyzerSession = () => {
  localStorage.removeItem('predictAnalytics')
  localStorage.removeItem('analysisDashboard')
  localStorage.removeItem('analyzerTiloData')
  localStorage.removeItem('userData')
  localStorage.removeItem('troiatec')
  localStorage.removeItem('accessToken')
  localStorage.removeItem('refreshToken')
}

/**
 ** This function is used for demo purpose route navigation
 ** In real app you won't need this function because your app will navigate to same route for each users regardless of ability
 ** Please note role field is just for showing purpose it's not used by anything in frontend
 ** We are checking role just for ease
 * ? NOTE: If you have different pages to navigate based on user ability then this function can be useful. However, you need to update it.
 * @param {String|number} userRole Role of user
 */
export const getHomeRouteForLoggedInUser = userRole => {
  if (userRole === 'admin' || userRole < 4) {
    if (Number(userRole) === 0 || (Number(userRole) === 'NaN' && getUserData().id_rol === 0)) {
      return '/S-home'
    } else {
      return '/home'
    }
  }
  if (userRole === 'client' || userRole >= 4) return '/imageAnalyzer'
  return '/login'
}

export const getHomeRouteForLoggedInUserBabel = userPermission => {
  const hasDashboardPermission = Array.isArray(userPermission)
  if (hasDashboardPermission) {
    if (userPermission?.includes(permissionsFormated.dashboard)) return '/home'

    if (userPermission?.includes(permissionsFormated.soporte)) return '/S-home'

    if (!userPermission?.includes(permissionsFormated.dashboard) && userPermission?.includes(permissionsFormated.image_analyzer)) return '/imageAnalyzer'
  }

  if (isUserLoggedIn() === null || localStorage.getItem("troiatec") === null) {
    clearAnalyzerSession()
  }

  return '/login'
}

// ** React Select Theme Colors
export const selectThemeColors = theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: '#7367f01a', // for option hover bg-color
    primary: '#7367f0', // for selected option bg-color
    neutral10: '#7367f0', // for tags bg-color
    neutral20: '#ededed', // for input border-color
    neutral30: '#ededed' // for input hover border-color
  }
})

// ** Verify if value exist in variable
export const searchInVar = (variable, searchedValue) => {
  if (typeof variable === 'string') {
    return variable === searchedValue
  } else {
    return variable.find(el => el === searchedValue)
  }
}

// ** approx for cogtinive and clarity range values
export const approx = (value) => {
  if (value <= 25) {
    return 25
  } else if (value <= 50) {
    return 50
  } else if (value <= 75) {
    return 75
  }

  return 100
}

// ** colors for cognitive Demand
export const CognitiveDemandColors = (position) => {
  //const arr = ["#258737", "#5ac77b", "#f0c943", "#ff6a61"]
  const arr = ["#ff6a61", "#f0c943", "#5ac77b", "#258737"]
  return arr[position]
}

// ** colors for Focus
export const FocusColors = (position) => {
  const arr = ["#ff6a61", "#f0c943", "#5ac77b", "#258737"]
  return arr[position]
}

// ** Calc Effectivity
export const calcEff = (CC, F) => {
  if (CC !== null & F !== null) {
    return Number(((Number(F) + (100 - Number(CC))) / 2).toFixed(2))
  }
  return null
}

// ** Window Dimensions
export const getWindowDimensions = () => {
  const width = window.innerWidth
  const height = window.innerHeight
  return {
    width,
    height
  }
}

// ** Sliders Functions
export const toFunction = (value) => {
  return `${value}%`
}

export const fromFunction = (value) => {
  return value
}

// ** Function to handle Pagination
const handlePagination = (setCurrentPage, page) => {
  setCurrentPage(page.selected)
}

// ** Custom Pagination
import ReactPaginate from 'react-paginate'
import "@styles/react/libs/tables/react-dataTable-component.scss"
export const CustomPagination = (currentPage, setCurrentPage, pCount, separated = false) => (
  <ReactPaginate
    previousLabel=''
    nextLabel=''
    forcePage={currentPage}
    onPageChange={page => handlePagination(setCurrentPage, page)}
    pageCount={pCount || 1}
    breakLabel='...'
    pageRangeDisplayed={2}
    marginPagesDisplayed={2}
    activeClassName='active'
    pageClassName='page-item'
    breakClassName='page-item'
    breakLinkClassName='page-link'
    nextLinkClassName='page-link'
    nextClassName='page-item next'
    previousClassName='page-item prev'
    previousLinkClassName='page-link'
    pageLinkClassName='page-link'
    containerClassName={`pagination react-paginate ${separated ? 'separated-pagination' : ''} pagination-sm justify-content-end pr-1 mt-1`}
  />
)
export const getDashData = (dispatch, Babel, callback) => {

  const newAxios = axios.create()
  newAxios.get(`${process.env.REACT_APP_ADMIN_URL}/suscription/me`, {
    headers: {
      Authorization: `Bearer ${Babel?.access_token}`
    }
  }).then((babelres) => {
    const babelData = babelres.data
    const fData = new FormData()
    fData.append("start", babelData.start_date)
    fData.append("end", babelData.end_date)

    axios.post(`${process.env.REACT_APP_BACK_URL}User/dashboard`, fData).then((res) => {

      const analyzerData = res.data
      if (analyzerData && analyzerData.stimulus_count) {
        analyzerData.stimulus_count.extra_stimulus = babelData.extra_credits
        analyzerData.stimulus_count.maximum_stimulus = babelData.credits
        analyzerData.stimulus_count.percent = Math.round((Number(analyzerData.stimulus_count.count) * 100) / (Number(babelData.extra_credits) + Number(babelData.credits)))
      }

      dispatch(handleDashboard(analyzerData))

      if (callback) {
        callback(analyzerData)
      }
    })
  })
}

// esta funcion permite obtener y actualizar los permisos dónde sea correspondiente
// esta pensada para ser llamada únicamente para apartados que requieran permisos de modulos completos
// no para funciones individuales
export const fetchPermissions = (babel, dispatch, userData, ability) => {
  return new Promise((resolve, reject) => {
    const newAxios = axios.create()
    newAxios.get(`${process.env.REACT_APP_ADMIN_URL}/permission/me`, {
      headers: {
        Authorization: `Bearer ${babel?.access_token}`
      }
    }).then((res) => {
      if (res.status === 200) {
        const viejo = babel
        viejo.permissions = res.data
        dispatch(handleBabel(viejo))

        const troiatec = JSON.parse(localStorage.getItem("troiatec"))

        if (troiatec && typeof troiatec === 'object') {
          troiatec["permissions"] = res.data
          localStorage.setItem('troiatec', JSON.stringify(troiatec))
        }

        let abilityUser = []

        usersViewConfigBabel.forEach((obj) => {
          if (res.data.includes(obj.apartado) || obj.apartado === 'all') {
            const new_obj = {
              action: obj.action,
              subject: obj.subject
            }
            abilityUser.push(new_obj)
          } else if (babel.permissions.includes(permissionsFormated.soporte)) { // pueden cambiar contrasenia, ver settings y perfil
            const new_obj = [
              {
                action: 'read',
                subject: 'Support'
              },
              {
                action: 'read',
                subject: 'changePass'
              },
              {
                action: 'read',
                subject: 'profile'
              }
            ]
            abilityUser = new_obj
          }

        })

        const userDviejo = userData

        userDviejo.ability = abilityUser
        dispatch(handleUserData(userDviejo))
        ability.update(abilityUser)

        const userd = JSON.parse(localStorage.getItem("userData"))

        if (userd && typeof userd === 'object') {
          userd["ability"] = abilityUser
          localStorage.setItem('userData', JSON.stringify(userd))
        }

        // Resolvemos la promesa una vez que hayamos completado todas las acciones
        resolve(viejo)
      } else {
        // Si la respuesta no es 200, rechazamos la promesa
        reject(new Error('La solicitud no fue exitosa'))
      }
    }).catch(error => {
      // Si ocurre un error durante la solicitud, rechazamos la promesa
      reject(error)
    })
  })
}

export const convertirImagenBase64 = (url) => {
  const nuevaInstancia = axios.create()
  return nuevaInstancia.get(url, { responseType: 'blob' }) // Indicar a Axios que espere un blob como respuesta
    .then(response => {
      const blob = response.data
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
    })
    .catch(error => {
      console.error('Error al convertir la imagen a base64:', error)
      throw error
    })
}

export const obtenerDimensionesImagenDesdeBase64 = async (base64Data) => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = function () {
      // Una vez que la imagen se haya cargado completamente, resolvemos la promesa con sus dimensiones
      resolve({
        width: img.width,
        height: img.height
      })
    }
    img.onerror = function () {
      // Si hay un error al cargar la imagen, rechazamos la promesa con el error
      reject(new Error('Error al cargar la imagen.'))
    }
    img.src = base64Data // Establecer los datos base64 de la imagen
  })
}

// Esta funcion permite obtener los permisos y retornarlos
// pensado para funciones específicas que requieren saber el permiso actual
// No esta pensado para modulos grandes.
export const getPermissions = (babel) => {
  return new Promise((resolve, reject) => {
    const newAxios = axios.create()
    newAxios.get(`${process.env.REACT_APP_ADMIN_URL}/permission/me`, {
      headers: {
        Authorization: `Bearer ${babel?.access_token}`
      }
    }).then((res) => {
      if (res.status === 200) {
        // Resolvemos la promesa una vez que hayamos completado todas las acciones
        resolve(res.data)
      } else {
        // Si la respuesta no es 200, rechazamos la promesa
        reject(new Error('La solicitud no fue exitosa'))
      }
    }).catch(error => {
      // Si ocurre un error durante la solicitud, rechazamos la promesa
      reject(error)
    })
  })
}

export const checkIsUnlimited = async (Babel) => {
  try {
    const newAxios = axios.create()
    const res = await newAxios.get(`${process.env.REACT_APP_ADMIN_URL}/suscription/me`, {
      headers: {
        Authorization: `Bearer ${Babel?.access_token}`
      }
    })

    if (res.status === 200) {
      const data = res.data
      //return Number(data.unlimited) === 1
      return data.credits === null
    } else {
      throw new Error('Unexpected response status')
    }
  } catch (error) {
    console.error('Error checking unlimited status:', error)
    throw error
  }
}
